
import { Component, Watch } from 'vue-property-decorator';
import { loadView, loadWidget } from '@/utils/helpers';
import { brandName } from '@/utils/brand';
import { Getter, namespace } from 'vuex-class';
import {
  IEventData,
  IPayments,
  IPaymentStatusIndicatorResponse,
  ISideBar,
  IUser,
} from '@/types/types';
import { links } from '@/data/sidebar';
import { mixins } from 'vue-class-component';
import WidgetMixins from '@/mixins/WidgetMixins';
import DialogMixins from '@/mixins/DialogMixins';
import Pusher from 'pusher-js';
import moment from 'moment';

const authModule = namespace('auth');

@Component({
  components: {
    'app--bar': loadView(`NavBar`),
    'app--drawer': loadView(`SideBar`),
    SideBarDrawer: loadView('SideBarDrawer'),
    ProfileMenu: loadWidget('widgets/ProfileMenu'),
    TopUpDialog: loadView('pages/payments/dialogs/TopUpDialog'),
    HelpDialog: loadView('pages/help-center/HelpDialog'),
    snackBar: loadWidget('widgets/SnackBar'),
  },
})
export default class Master extends mixins(DialogMixins, WidgetMixins) {
  @Getter('getNavigationDrawerState') drawerState!: boolean;
  @authModule.Getter('getUserType') userType!: string;
  @authModule.Getter('getUserDetails') user!: IUser;
  state = true;
  menu = false;

  emitDrawer(value: boolean): void {
    this.state = value;
  }

  closeDrawer(state: boolean): void {
    this.$store.dispatch('navigationDrawer', state, { root: true });
  }
  /* first four real links for the phone bar; section labels, "soon" rows
     and reseller-only items the account cannot see are skipped */
  get bottomLinks(): ISideBar[] {
    return (this.links as ISideBar[])
      .filter(
        (item) =>
          !!item.link &&
          !item.soon &&
          (!item.isReseller || (this.user && this.user.businessId !== null))
      )
      .slice(0, 4);
  }

  sidebarLinks(key: string): Array<ISideBar | { label: string }> {
    return links[key];
  }

  /* the nav for this user: sub-accounts lose billing items, and a section
     label left with nothing under it goes too */
  get links(): Array<ISideBar | { label: string }> {
    const all = (this.sidebarLinks(this.userType) ?? []) as ISideBar[];
    if (!(this.user && this.user.isSubAccount)) {
      return all;
    }
    const kept = all.filter((item) => !item.hideForSubAccount);
    return kept.filter((item, idx) => {
      if (item.link) {
        return true;
      }
      const next = kept[idx + 1];
      return !!next && !!next.link;
    });
  }

  /* the router guard covers navigation; this covers a sub-account whose
     profile arrives after it already landed on a billing page */
  @Watch('user', { immediate: true })
  onUserChange(user: IUser | null): void {
    if (
      user &&
      user.isSubAccount &&
      this.$route.matched.some((record) => record.meta.hideForSubAccount)
    ) {
      this.$router.replace({ name: 'account.dashboard' }).catch(() => ({}));
    }
  }

  /**
   * Top up now runs through billing: the amount raises an invoice, and the
   * customer pays that invoice. This keeps every purchase backed by a
   * numbered document instead of a bare payment with nothing to show for it.
   */
  async initiatePayment(payload: IPayments): Promise<void> {
    const invoice = await this.$store.dispatch('invoices/issue', {
      amount: Number(payload.amount),
    });
    this.close({ idx: 'payment', state: false });
    if (invoice) {
      this.$router
        .push({ name: 'account.invoices', query: { invoice: `${invoice.id}` } })
        .catch(() => {
          /* already on the invoices page */
        });
    }
  }

  created(): void {
    this.$store.dispatch('auth/me');
    this.$store.dispatch('checkCreditBalance');
    const pusher = new Pusher('09291eb6a82009bab6b5', {
      cluster: 'eu',
    });

    const channel = pusher.subscribe('kairos-dashboard');
    channel.bind('sms-credit-event', (data: IEventData) => {
      console.log(`Event ::: ${data}`);
      if (
        this.user &&
        (data.user.id === this.user.id || data.user.email === this.user.email)
      ) {
        this.$store.dispatch('inProgress', false, { root: true });
        if (!data.success) {
          this.$store.commit(
            'payments/UPDATE_PAYMENT_RESPONSE',
            {
              title: 'Payment Failed',
              subTitle:
                'Oops, it looks like your payment was unable to complete.Try again',
              icon: require('@/assets/images/attention.png'),
            } as IPaymentStatusIndicatorResponse,
            { root: true }
          );
        } else {
          this.$store.dispatch('checkCreditBalance', null, { root: true });
          this.$store.commit(
            'payments/UPDATE_PAYMENT_RESPONSE',
            {
              title: 'Payment Completed',
              subTitle: `Your payment was processed successfully and your account will be credited with ${
                data.totalCredit
              } credit units.Thank you for choosing ${brandName()}.`,
              icon: require('@/assets/images/check.png'),
            } as IPaymentStatusIndicatorResponse,
            { root: true }
          );
        }
        this.$store.commit('payments/UPDATE_PAYMENT_DETAILS', {
          status: data.paymentResponse.status,
          amount: data.paymentResponse.amount,
          reference: data.paymentResponse.reference,
          transactionId: data.paymentResponse.reference,
        });
      }
    });

    channel.bind(
      'bulk-contact-upload',
      (data: { isCompleted: boolean; isSuccess: boolean; error?: string }) => {
        if (!data.isSuccess && data.error) {
          this.$store.dispatch(
            'snackBarMessage',
            data.error ?? 'Bulk contacts upload failed due to an error',
            { root: true }
          );
          this.$store.dispatch('snackBarVisibility', true, { root: true });
        } else {
          this.$store.dispatch('contact/list', {
            page: 1,
            limit: 30,
          });
        }
        this.$store.dispatch('inProgress', !data.isCompleted, { root: true });
        this.$store.dispatch('isLoading', false, { root: true });
      }
    );

    channel.bind(
      'bulk-logs-download',
      (data: {
        isCompleted: boolean;
        isSuccess: boolean;
        message: string;
        accountId: number;
      }) => {
        console.log(data);
        this.$store.dispatch('totalBulkRecords', 0, { root: true });
        this.$store.dispatch('isDownloading', false, { root: true });
        this.$store.dispatch('snackBarMessage', data?.message, { root: true });
        this.$store.dispatch('snackBarVisibility', true, { root: true });
        this.$store.commit('UPDATE_TIMEOUT', 10000, { root: true });
        if (this.$route.name === 'account.downloads') {
          this.$store.dispatch('log/listAllFileDownloads', {
            page: 1,
            limit: 15,
            query: `&date=${moment()
              .subtract(1, 'month')
              .format('YYYY-MM-DD')},${moment().format('YYYY-MM-DD')}`,
            subAccountId: data.accountId,
          });
        }
      }
    );
  }
}
